
import {
  defineComponent,
  useMeta,
  useContext,
  useStore,
  computed,
  useRoute,
  watch,
} from '@nuxtjs/composition-api'
import { GoogleTagManager } from '~/utils/google-tag-manager'
import { domainByCountry } from '~/assets/ts/Domains'
import { getCountryCode } from '~/assets/ts/Locale'

let acredoGoogleTagManager: GoogleTagManager
let gtmConsentGranted = false


export default defineComponent({
  head: {},
  props: {
    pageRoute: {
      type: String,
      required: true,
    },
    image: {
      type: String,
      required: false,
    },
    individualTitle: {
      type: String,
      required: false,
      default: '',
    },
    parameter: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
    productId: {
      type: String,
      required: false,
      default() {
        return ''
      },
    },
  },
  setup: (props: any) => {
    const {
      app: { i18n, router },
    } = useContext()

    const country: string = getCountryCode(true)

    const pageRoute = props.pageRoute
    let pageName = ''
    let pageStr = ''

    if (pageRoute.search('___') !== -1) {
      pageName = pageRoute.substr(0, pageRoute.length - 5)

      if (pageName.search('___') !== -1) {
        pageName = pageName.substr(0, pageName.length - 5)
      }

      pageStr = 'pages.' + pageName
    }

    const pageTitle =
      props.individualTitle !== ''
        ? props.individualTitle
        : i18n.t(pageStr + '.title')

    /**
     * Opengraph / Twitter image
     */

    let individualImage: string = props.image ? props.image : ''

    const openGraphTermsImages: { [key: string]: string } = {
      about: 'acredo_about.jpg',
      engagement: 'acredo_engagement.jpg',
      diamondJewelry: 'acredo_jewelry.jpg',
      shops: 'acredo_locations.jpg',
      memoire: 'acredo_memoires.jpg',
      wedding: 'acredo_wedding.jpg',
    }

    for (const termIdx in openGraphTermsImages) {
      if (pageRoute.substr(0, termIdx.length) === termIdx) {
        individualImage = openGraphTermsImages[termIdx]
      }
    }

    const openGraphImage: string =
      domainByCountry(country) +
      '/img/opengraph/' +
      (individualImage !== '' ? individualImage : 'acredo.jpg')

    const metaLinks = [
      {
        hid: 'canonical',
        rel: 'canonical',
        href: domainByCountry(country) + router?.currentRoute.path,
      },
      {
        hid: 'alternate',
        rel: 'alternate',
        href: domainByCountry(country) + router?.currentRoute.path,
        hreflang: i18n.locale + '-' + country.toUpperCase(),
      },
    ]

    if (country === 'de' || country === 'at' || country === 'ch') {
      if (country !== 'de') {
        metaLinks.push({
          hid: 'alternate',
          rel: 'alternate',
          href: domainByCountry('de') + router?.currentRoute.path,
          hreflang: i18n.locale + '-DE',
        })
      }

      if (country !== 'at') {
        metaLinks.push({
          hid: 'alternate',
          rel: 'alternate',
          href: domainByCountry('at') + router?.currentRoute.path,
          hreflang: i18n.locale + '-AT',
        })
      }

      if (country !== 'ch') {
        metaLinks.push({
          hid: 'alternate',
          rel: 'alternate',
          href: domainByCountry('ch') + router?.currentRoute.path,
          hreflang: i18n.locale + '-CH',
        })
      }
    }

    const route = useRoute()

    let siteUrl = 'https://www.' + process.env.NUXT_ENV_DOMAIN

    if (process.env.APP_ENV === 'dev') {
        siteUrl = 'http://' + process.env.NUXT_ENV_DOMAIN
    }

    const ogUrl = computed(() =>
      process.client ? window.location.href : siteUrl + route.value.fullPath
    )

    useMeta({
      htmlAttrs: {
        lang: i18n.locale + '-' + country.toUpperCase(),
      },
      link: metaLinks,
      title: pageTitle as string,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: i18n.t(pageStr + '.description', props.parameter) as string,
        },
        {
          hid: 'google-site-verification',
          name: 'google-site-verification',
          content: process.env.googleWebmasterToolsKey
            ? process.env.googleWebmasterToolsKey[country as any]
            : '',
        },
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: i18n.t(pageStr + '.title', props.parameter) as string,
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: i18n.t(pageStr + '.description', props.parameter) as string,
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: openGraphImage,
        },
        {
          hid: 'twitter:image:alt',
          name: 'twitter:image:alt',
          content: i18n.t(pageStr + '.title', props.parameter) as string,
        },
        {
            hid: 'og:url',
            property: 'og:url',
            content: ogUrl.value,
          },
        {
          hid: 'og:title',
          property: 'og:title',
          content: i18n.t(pageStr + '.title', props.parameter) as string,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'website',
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'acredo',
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: i18n.t(pageStr + '.description', props.parameter) as string,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: openGraphImage,
        },
        {
          hid: 'og:image:secure_url',
          property: 'og:image:secure_url',
          content: openGraphImage,
        },
        {
          hid: 'og:image:alt',
          property: 'og:image:alt',
          content: i18n.t(pageStr + '.title', props.parameter) as string,
        },
        {
          hid: 'og:locale',
          property: 'og:locale',
          content: country as string,
        },
      ],
    })

    const store = useStore()

    /* Google Tag Manager: load in any case (Consent Mode). If cookies already allowed (persisted), start with granted to avoid a pre-granted denied event & premature page_view. */
    const marketingInitiallyAllowed = process.client
      ? !!store.getters['cookieConsent/areMarketingCookiesAllowed']
      : false

    if (process.client && !acredoGoogleTagManager) {
      try {
        const consentDefaults: { ad_storage: 'granted' | 'denied'; analytics_storage: 'granted' | 'denied' } = marketingInitiallyAllowed
          ? { ad_storage: 'granted', analytics_storage: 'granted' }
          : { ad_storage: 'denied', analytics_storage: 'denied' }

        acredoGoogleTagManager = new GoogleTagManager(getCountryCode(true), {
          autoPageView: false,
          enabledInDev: true,
          debug: false,
          consentDefaults,
        })
        if (marketingInitiallyAllowed) {
          gtmConsentGranted = true // Mark as already granted to prevent duplicate consent_update
        }
        // Make sure a visible log is printed when debug cookie is present (console.info is visible by default)
        if (document.cookie.includes('debugTracking=yes')) {
          console.debug('[GTM] instance created', {
            marketingInitiallyAllowed,
            consentDefaults,
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug('[GTM] init error', e)
      }
    }

    const sendAllPageViews = () => {
      if (!process.client) return
      const marketingAllowed = store.getters['cookieConsent/areMarketingCookiesAllowed']
      const currentPath = window.location.pathname

      if (store.getters['tracking/getLastUrl'] === currentPath) return

      // Check if shop login is active - if so, don't send any tracking events
      const shopLoginActive = store.getters['shoplogin/isActive']
      if (shopLoginActive) {
        if (document.cookie.includes('debugTracking=yes')) {
          // eslint-disable-next-line no-console
          console.info('[GTM] page_view suppressed due to active shop login', { currentPath })
        }
        return
      }

      // Always allow GTM page_view only if marketing consent OR no consent needed (adjust logic if you have such a flag)
      if (acredoGoogleTagManager && marketingAllowed) {
        acredoGoogleTagManager.sendPageView(pageTitle as string, currentPath)
        if (document.cookie.includes('debugTracking=yes')) {
          // eslint-disable-next-line no-console
          console.info('[GTM] page_view sent', { currentPath, pageTitle })
        }
      }

      if (marketingAllowed) {
        store.commit('tracking/setLastUrl', currentPath)
      } else if (document.cookie.includes('debugTracking=yes')) {
        // eslint-disable-next-line no-console
        console.info('[GTM] page_view suppressed due to marketing consent not granted', { currentPath })
      }
    }

    const areMarketingCookiesAllow = computed(() => {
      const allowed = store.getters['cookieConsent/areMarketingCookiesAllowed']
      if (allowed && !gtmConsentGranted) {
        try {
          if (acredoGoogleTagManager) {
            acredoGoogleTagManager.setConsent({
              ad_storage: 'granted',
              analytics_storage: 'granted',
            })
            gtmConsentGranted = true
          }
        } catch (e) {
          // eslint-disable-next-line no-console
            console.debug('GTM consent update error', e)
        }
        // initial page view after consent
        sendAllPageViews()
      }
      return allowed
    })

    // React to route changes (after consent) for SPA navigation
    watch(
      () => route.value.fullPath,
      () => {
        if (areMarketingCookiesAllow.value) {
          sendAllPageViews()
        }
      }
    )

    // Initial Trigger: if consent already granted (user accepted earlier and state persisted)
    if (process.client && marketingInitiallyAllowed) {
      // Now that GTM started with granted defaults, explicitly trigger first page view.
      sendAllPageViews()
    }

    return {
      country,
      areMarketingCookiesAllow,
    }
  },
})
